import React from 'react'
import {
  Box,
  Grid,
  Button,
  TextField,
  Typography,
  Autocomplete,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Divider,
  Checkbox,
  FormControlLabel,
  IconButton,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import { getRequest, postRequest, putRequest } from 'Includes/api/apicall'
import { GET_URL, POST_URL, PUT_URL } from 'Includes/urls'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { mergeConceptOptions, normalizeConceptsList } from './visitConcepts'

const emptyForm = () => ({
  company: null,
  description: '',
  visit_type: '',
  visit_date: '',
  visit_time: '',
  priority: 'medium',
  status: 'pending',
  postponed_date: '',
  postponed_time: '',
  assigned_to: null,
})

function VisitForm(props) {
  const navigate = useNavigate()
  const [saving, setSaving] = React.useState(false)
  const [companies, setCompanies] = React.useState([])
  const [users, setUsers] = React.useState([])
  const [form, setForm] = React.useState(emptyForm)
  const [errors, setErrors] = React.useState({})
  const [visitTypes, setVisitTypes] = React.useState(['Staff Orientation', 'Student Orientation'])
  const [conceptOptions, setConceptOptions] = React.useState([])
  const [selectedConcepts, setSelectedConcepts] = React.useState([])
  const [newConceptInput, setNewConceptInput] = React.useState('')
  const [searchParams] = useSearchParams()
  const editId = searchParams.get('id')

  const userInfo = React.useMemo(() => JSON.parse(localStorage.getItem('user') || '{}'), [])
  const allowedCodes = React.useMemo(() => {
    if (Array.isArray(userInfo?.permission_codenames) && userInfo.permission_codenames.length) {
      return new Set(userInfo.permission_codenames.filter(Boolean))
    }
    const ownCodes = (userInfo?.user_permissions || userInfo?.permissions || []).map((p) =>
      typeof p === 'string' ? p : p?.codename
    )
    const groupCodes = (userInfo?.groups || [])
      .flatMap((g) => g?.permissions || [])
      .map((p) => (typeof p === 'string' ? p : p?.codename))
    return new Set([...ownCodes, ...groupCodes].filter(Boolean))
  }, [userInfo])
  const isSuperuser = Boolean(userInfo?.is_superuser)
  const canAdd = isSuperuser || allowedCodes.has('visible_company_visit_add')

  React.useEffect(() => {
    if (isSuperuser || canAdd) return
    navigate('/visits/list', { replace: true })
  }, [isSuperuser, canAdd, navigate])

  const loadCompanies = () => {
    getRequest(GET_URL.company.api, { limit: 500, pageno: 1 }, props).then((response) => {
      const data = response?.data?.data || []
      setCompanies(Array.isArray(data) ? data : [])
    })
  }

  const loadUsers = () => {
    getRequest(GET_URL.user.api, { limit: 300, pageno: 1, status: 'active' }, props).then((response) => {
      const data = response?.data?.data || []
      setUsers(Array.isArray(data) ? data : [])
    })
  }

  const loadVisitTypes = () => {
    getRequest(`${GET_URL.company_visits.api}types/`, {}, props).then((response) => {
      if (response?.status === 200 && response.data?.data) {
        const types = response.data.data;
        const merged = Array.from(new Set(['Staff Orientation', 'Student Orientation', ...types]));
        setVisitTypes(merged);
      }
    }).catch(() => {})
  }

  const loadConceptOptions = () => {
    getRequest(`${GET_URL.company_visits.api}concepts/`, {}, props).then((response) => {
      if (response?.status === 200 && response.data?.data) {
        setConceptOptions(mergeConceptOptions(response.data.data, selectedConcepts))
      }
    }).catch(() => {})
  }

  const addNewConcept = () => {
    const label = String(newConceptInput || '').trim()
    if (!label) return
    setConceptOptions((prev) => mergeConceptOptions(prev, [label]))
    setSelectedConcepts((prev) => {
      const key = label.toLowerCase()
      if (prev.some((c) => c.toLowerCase() === key)) return prev
      return [...prev, label]
    })
    setNewConceptInput('')
  }

  const toggleConcept = (label) => {
    const key = label.toLowerCase()
    setSelectedConcepts((prev) => {
      const has = prev.some((c) => c.toLowerCase() === key)
      if (has) return prev.filter((c) => c.toLowerCase() !== key)
      return [...prev, label]
    })
  }

  React.useEffect(() => {
    loadCompanies()
    loadUsers()
    loadVisitTypes()
    loadConceptOptions()
    if (editId) {
      getRequest(`${GET_URL.company_visits.api}${editId}/`, {}, props).then((response) => {
        if (response?.status === 200 && response.data) {
          const d = response.data?.data || response.data;
          setForm((prev) => ({
            ...prev,
            company: d.company ? { id: d.company, name: d.company_name } : null,
            description: d.description || '',
            visit_type: d.visit_type || '',
            visit_date: d.visit_date || '',
            visit_time: d.visit_time ? d.visit_time.slice(0, 5) : '',
            priority: d.priority || 'medium',
            assigned_to: d.assigned_to ? { id: d.assigned_to, username: d.assigned_to_name } : null,
            status: d.status || 'pending',
            postponed_date: d.postponed_date || '',
            postponed_time: d.postponed_time ? d.postponed_time.slice(0, 5) : '',
          }))
          const concepts = normalizeConceptsList(d.concepts_to_explain)
          setSelectedConcepts(concepts)
          setConceptOptions((prev) => mergeConceptOptions(prev, concepts))
        }
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId])

  const validate = () => {
    const next = {}
    if (!form.company?.id) next.company = 'Company is required'
    if (!form.visit_date) next.visit_date = 'Date is required'
    if (form.status === 'postponed') {
      if (!form.postponed_date) next.postponed_date = 'Postponed date is required'
      if (!form.postponed_time) next.postponed_time = 'Postponed time is required'
    }
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const handleSubmit = async () => {
    if (!validate()) return
    setSaving(true)
    try {
      const payload = {
        company: form.company.id,
        description: String(form.description || '').trim(),
        visit_type: String(form.visit_type || '').trim(),
        visit_date: form.visit_date,
        priority: form.priority,
        status: 'pending',
        assigned_to: form.assigned_to?.id ?? null,
        concepts_to_explain: normalizeConceptsList(selectedConcepts),
      }
      if (form.visit_time) {
        payload.visit_time = form.visit_time.length === 5 ? `${form.visit_time}:00` : form.visit_time
      } else {
        payload.visit_time = null
      }
      if (editId) {
        payload.status = form.status || 'pending'
        if (form.status === 'postponed') {
          payload.postponed_date = form.postponed_date
          payload.postponed_time = form.postponed_time.length === 5 ? `${form.postponed_time}:00` : form.postponed_time
        }
        await putRequest(`${PUT_URL.company_visits.api}${editId}/`, payload, props)
      } else {
        await postRequest(POST_URL.company_visits.api, payload, props)
      }
      navigate('/visits/list')
    } finally {
      setSaving(false)
    }
  }

  const sectionTitleSx = {
    mb: 2.5,
    pl: 1.75,
    borderLeft: '4px solid #4680FF',
    color: '#4680FF',
    fontWeight: 600,
    fontSize: { xs: '1rem', sm: '1.05rem' },
    lineHeight: 1.35,
  }

  const sectionPaperSx = {
    p: { xs: 2, sm: 2.5, md: 3 },
    borderRadius: 2,
    border: '1px solid',
    borderColor: 'divider',
    bgcolor: 'background.paper',
    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.06)',
  }

  return (
    <Box p={2}>
      <Grid container alignItems="center" spacing={2}>
        <Grid item md={6} xs={12} mt={2}>
          <Typography variant="h5" fontWeight={600}>
            {editId ? 'Edit visit' : 'Add visit'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            New visits are saved as Pending. Assign someone optionally, or use Assign on the visit list.
          </Typography>
        </Grid>
        <Grid item md={6} xs={12}>
          <Box display="flex" justifyContent={{ xs: 'flex-start', md: 'flex-end' }} gap={1} flexWrap="wrap">
            <Button
              variant="contained"
              component={Link}
              to="/visits/list"
              sx={{ textTransform: 'none' }}
            >
              <VisibilityOutlinedIcon sx={{ mr: 1 }} />
              View visits
            </Button>
          </Box>
        </Grid>
      </Grid>

      <Box mt={3} mb={3}>
        <Divider />
      </Box>

      <Box sx={sectionPaperSx}>
        <Typography variant="subtitle1" component="h2" sx={sectionTitleSx}>
          Visit details
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {editId ? 'Update the visit information below.' : 'Fill in the visit information below. Status is always Pending when created.'}
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Autocomplete
              options={companies}
              isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
              getOptionLabel={(o) => (o?.name != null ? String(o.name) : '')}
              value={form.company?.id ? form.company : null}
              onChange={(_, v) => setForm((f) => ({ ...f, company: v }))}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Company"
                  required
                  error={Boolean(errors.company)}
                  helperText={errors.company}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Autocomplete
              options={users}
              isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
              getOptionLabel={(o) => o?.username || o?.email || String(o?.id || '')}
              value={form.assigned_to?.id ? form.assigned_to : null}
              onChange={(_, v) => setForm((f) => ({ ...f, assigned_to: v }))}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Assigned to (optional)"
                  helperText="Assign now or later from the list"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Autocomplete
              freeSolo
              options={visitTypes}
              value={form.visit_type || ''}
              onChange={(_, v) => setForm((f) => ({ ...f, visit_type: v }))}
              onInputChange={(_, v) => setForm((f) => ({ ...f, visit_type: v }))}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Visit Type"
                  helperText="Select or type a new visit type"
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Description"
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              error={Boolean(errors.description)}
              helperText={errors.description}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              type="date"
              label="Visit date"
              InputLabelProps={{ shrink: true }}
              value={form.visit_date}
              onChange={(e) => setForm((f) => ({ ...f, visit_date: e.target.value }))}
              error={Boolean(errors.visit_date)}
              helperText={errors.visit_date || 'dd-mm-yyyy'}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              type="time"
              label="Visit time"
              InputLabelProps={{ shrink: true }}
              value={form.visit_time}
              onChange={(e) => setForm((f) => ({ ...f, visit_time: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Priority</InputLabel>
              <Select
                label="Priority"
                value={form.priority}
                onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))}
              >
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="high">High</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          {editId && (
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={form.status || 'pending'}
                  onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                >
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="visited">Visited</MenuItem>
                  <MenuItem value="postponed">Postponed</MenuItem>
                  <MenuItem value="cancel">Canceled</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          )}
          {editId && form.status === 'postponed' && (
            <>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="date"
                  label="Postponed to (date)"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ lang: 'en-GB' }}
                  value={form.postponed_date}
                  onChange={(e) => setForm((f) => ({ ...f, postponed_date: e.target.value }))}
                  error={Boolean(errors.postponed_date)}
                  helperText={errors.postponed_date || 'dd-mm-yyyy'}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="time"
                  label="Postponed to (time)"
                  InputLabelProps={{ shrink: true }}
                  value={form.postponed_time}
                  onChange={(e) => setForm((f) => ({ ...f, postponed_time: e.target.value }))}
                  error={Boolean(errors.postponed_time)}
                  helperText={errors.postponed_time}
                />
              </Grid>
            </>
          )}
        </Grid>

        <Box mt={3}>
          <Divider sx={{ mb: 2.5 }} />
          <Typography variant="subtitle1" component="h3" sx={{ ...sectionTitleSx, mb: 1.5 }}>
            Concepts to be explained
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select topics to cover during the visit. Add new concepts below; they appear as checkboxes when adding the school review.
          </Typography>
          <Grid container spacing={1}>
            {conceptOptions.length === 0 ? (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  No concepts yet. Add one below.
                </Typography>
              </Grid>
            ) : (
              conceptOptions.map((concept) => {
                const checked = selectedConcepts.some((c) => c.toLowerCase() === concept.toLowerCase())
                return (
                  <Grid item xs={12} sm={6} md={4} key={concept}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={checked}
                          onChange={() => toggleConcept(concept)}
                        />
                      }
                      label={concept}
                    />
                  </Grid>
                )
              })
            )}
            <Grid item xs={12}>
              <Box display="flex" gap={1} alignItems="flex-start" flexWrap="wrap" mt={1}>
                <TextField
                  size="small"
                  label="Add new concept"
                  placeholder="e.g. Fee collection"
                  value={newConceptInput}
                  onChange={(e) => setNewConceptInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      addNewConcept()
                    }
                  }}
                  sx={{ minWidth: 220, flex: 1 }}
                />
                <IconButton color="primary" onClick={addNewConcept} aria-label="Add concept">
                  <AddIcon />
                </IconButton>
              </Box>
            </Grid>
          </Grid>
        </Box>

        <Box mt={3} display="flex" justifyContent="flex-end" gap={2}>
          <Button variant="outlined" component={Link} to="/visits/list" sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSubmit} disabled={saving} sx={{ textTransform: 'none' }}>
            {saving ? 'Saving…' : editId ? 'Update visit' : 'Create visit'}
          </Button>
        </Box>
      </Box>
    </Box>
  )
}

export default VisitForm
